import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

// ============ Types ============

export interface SelfServiceInvitationResponse {
  invitation_id: string;
  email: string;
  pack_code: string;
  status: string;
}

export interface FreeTrialStatusResponse {
  has_used_free_trial: boolean;
}

// ============ Service Hook ============

export const useOnboardingService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const getAuthHeader = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  /**
   * Create a self-service invitation for a user signing up from landing page.
   * No authentication required - just saving the expressed pack interest.
   */
  const createSelfServiceInvitation = useCallback(async (
    email: string,
    packCode: string
  ): Promise<SelfServiceInvitationResponse> => {
    console.log('[OnboardingService] Creating self-service invitation:', { email, packCode, apiUrl });
    
    const response = await fetch(`${apiUrl}/api/onboarding/self-service-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        pack_code: packCode,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[OnboardingService] Failed to create invitation:', errorData);
      throw new Error(errorData.detail || 'Failed to create self-service invitation');
    }

    const data = await response.json();
    console.log('[OnboardingService] Invitation created successfully:', data);
    return data;
  }, [apiUrl]);

  /**
   * Get the pending self-service invitation for the authenticated user.
   */
  const getMyPendingInvitation = useCallback(async (): Promise<SelfServiceInvitationResponse | null> => {
    const authHeader = await getAuthHeader();

    const response = await fetch(`${apiUrl}/api/onboarding/my-pending-invitation`, {
      method: 'GET',
      headers: {
        ...authHeader,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to get pending invitation');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  /**
   * Check if the authenticated user has already used their free trial.
   */
  const checkFreeTrialStatus = useCallback(async (): Promise<FreeTrialStatusResponse> => {
    const authHeader = await getAuthHeader();

    const response = await fetch(`${apiUrl}/api/onboarding/free-trial-status`, {
      method: 'GET',
      headers: {
        ...authHeader,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to check free trial status');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  /**
   * Activate free trial for the authenticated user.
   */
  const activateFreeTrial = useCallback(async (): Promise<{ status: string; plan: string }> => {
    const authHeader = await getAuthHeader();

    const response = await fetch(`${apiUrl}/api/onboarding/activate-free-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to activate free trial');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  /**
   * Activate PAYG plan for the authenticated user (requires saved card).
   */
  const activatePayg = useCallback(async (): Promise<{ status: string; plan: string }> => {
    const authHeader = await getAuthHeader();

    const response = await fetch(`${apiUrl}/api/onboarding/activate-payg`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to activate Pay as you go plan');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  /**
   * Activate Gitex special trial for the authenticated user.
   */
  const activateGitexTrial = useCallback(
    async (withCard: boolean): Promise<{ status: string; plan: string }> => {
      const authHeader = await getAuthHeader();

      const response = await fetch(`${apiUrl}/api/onboarding/activate-gitex-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({ with_card: withCard }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to activate Gitex trial');
      }

      return response.json();
    },
    [apiUrl, getAuthHeader],
  );

  return {
    createSelfServiceInvitation,
    getMyPendingInvitation,
    checkFreeTrialStatus,
    activateFreeTrial,
    activatePayg,
    activateGitexTrial,
  };
};
